import zionDebug from '@fs/zion-debug'

const debug = zionDebug('rip:parseAiResponse')

// Conditional debug logging to avoid performance impact in production
const isProduction = process.env.NODE_ENV === 'production'

/**
 * Helper function to process section headers
 */
function processSectionHeader(lowerLine, line, sectionsEncountered) {
  if (lowerLine.includes('summary:')) {
    sectionsEncountered.summary = true
    return {
      section: 'summary',
      activeSection: 'summary',
      content: line
        .replace(/summary:/i, '')
        .trim()
        .replace(/\*+/g, ''),
    }
  }
  if (lowerLine.includes('dates:')) {
    sectionsEncountered.dates = true
    return { section: 'dates', activeSection: 'dates' }
  }
  if (lowerLine.includes('other persons mentioned:')) {
    sectionsEncountered.otherPersonsMentioned = true
    return { section: 'otherPersonsMentioned', activeSection: 'otherPersonsMentioned' }
  }
  if (lowerLine.includes('names and relationships:')) {
    sectionsEncountered.names = true
    return { section: 'namesAndRelationships', activeSection: 'names' }
  }
  return null
}

/**
 * Helper function to check if string contains only special characters
 */
const isSpecialCharacter = (str) => {
  // Check if the string contains only special characters (e.g., ●, *, etc.)
  return /^[^\w\s]+$/.test(str)
}

/**
 * Helper function to process list items
 * Note: The AI prompt explicitly requests list items to start with "-" (dash),
 * so we can safely assume all valid list items will begin with a dash.
 * Numbered lists (1., 2., etc.) should not be returned by the AI.
 */
function processListItem(section, line, lowerLine, dates, namesAndRelationships, otherPersonsMentioned) {
  if (!line.startsWith('-')) return false

  const trimmedLine = line.replace(/^-/, '').trim()
  if (!trimmedLine || isSpecialCharacter(trimmedLine)) return true

  if (section === 'dates' && !lowerLine.includes('[date')) {
    dates.push(trimmedLine)
  } else if (section === 'namesAndRelationships' && !lowerLine.includes('[name')) {
    namesAndRelationships.push(trimmedLine)
  } else if (section === 'otherPersonsMentioned' && !lowerLine.includes('[name')) {
    otherPersonsMentioned.push(trimmedLine)
  }
  return true
}

/**
 * Helper function to process line content within sections
 */
function processLineContent(lineData, sectionData, arrays) {
  const { line, index, lowerLine } = lineData
  const { section, summary, activeSection } = sectionData
  const { dates, namesAndRelationships, otherPersonsMentioned } = arrays

  const result = { summary, section, activeSection }

  if (section === 'summary') {
    result.summary += ` ${line}`
    return result
  }

  if (processListItem(section, line, lowerLine, dates, namesAndRelationships, otherPersonsMentioned)) {
    return result
  }

  // Handle first line without section header as summary
  if (shouldTreatAsFirstLineSummary(section, index, line, lowerLine)) {
    result.section = 'summary'
    result.activeSection = 'summary'
    result.summary = line.trim()
    return result
  }

  return result
}

/**
 * Helper function to check if line should be treated as first line summary
 */
function shouldTreatAsFirstLineSummary(section, index, line, lowerLine) {
  return (
    section === '' &&
    index === 0 &&
    line.trim() &&
    !lowerLine.includes('dates:') &&
    !lowerLine.includes('names and relationships:')
  )
}

/**
 * Helper function to create empty result object
 */
function createEmptyResult() {
  return { namesAndRelationships: [], otherPersonsMentioned: [], dates: [], summary: '' }
}

/**
 * Helper function to create sections tracking object
 */
function createSectionsTracking() {
  return { summary: false, dates: false, names: false, otherPersonsMentioned: false }
}

/**
 * Helper function to create result with sections tracking
 */
function createResultWithTracking(baseResult, activeSection, sectionsEncountered, currentSection) {
  return { ...baseResult, currentSection: activeSection || currentSection, sectionsEncountered }
}

/**
 * Helper function to handle empty transcript string
 */
function handleEmptyTranscript(trackSections, currentSection) {
  const baseResult = createEmptyResult()
  return trackSections
    ? createResultWithTracking(baseResult, null, createSectionsTracking(), currentSection)
    : baseResult
}

/**
 * Helper function to create base result from processed data
 */
function createBaseResult(processedResult) {
  const summary = processedResult.summary === '[summary]' ? '' : processedResult.summary.trim()

  return {
    namesAndRelationships:
      processedResult.namesAndRelationships.length > 0 ? processedResult.namesAndRelationships : [],
    otherPersonsMentioned:
      processedResult.otherPersonsMentioned.length > 0 ? processedResult.otherPersonsMentioned : [],
    dates: processedResult.dates.length > 0 ? processedResult.dates : [],
    summary,
  }
}

/**
 * Helper function to handle parsing errors
 */
function handleParsingError(error, transcriptString, trackSections, currentSection) {
  debug('Error parsing AI response:', error.message)

  const fallbackResult = {
    namesAndRelationships: [],
    otherPersonsMentioned: [],
    dates: [],
    summary: typeof transcriptString === 'string' ? transcriptString.slice(0, 500) : '',
  }

  return trackSections
    ? createResultWithTracking(fallbackResult, null, createSectionsTracking(), currentSection)
    : fallbackResult
}

/**
 * Helper function to finalize result based on tracking option
 */
function finalizeResult(baseResult, processedResult, trackSections, currentSection) {
  return trackSections
    ? createResultWithTracking(
        baseResult,
        processedResult.activeSection,
        processedResult.sectionsEncountered,
        currentSection
      )
    : baseResult
}

/**
 * Helper function to process all lines
 */
function processAllLines(lines, currentSection) {
  let summary = ''
  const dates = []
  const namesAndRelationships = []
  const otherPersonsMentioned = []
  let section = ''
  const sectionsEncountered = createSectionsTracking()
  let activeSection = currentSection

  lines.forEach((line, index) => {
    const lowerLine = line.toLowerCase()

    const headerResult = processSectionHeader(lowerLine, line, sectionsEncountered)
    if (headerResult) {
      section = headerResult.section
      activeSection = headerResult.activeSection
      if (headerResult.content !== undefined) {
        summary = headerResult.content
      }
      return
    }

    const contentResult = processLineContent(
      { line, index, lowerLine },
      { section, summary, activeSection },
      { dates, namesAndRelationships, otherPersonsMentioned }
    )
    section = contentResult.section
    activeSection = contentResult.activeSection
    summary = contentResult.summary
  })

  return { summary, dates, namesAndRelationships, otherPersonsMentioned, activeSection, sectionsEncountered }
}

/**
 * Unified parser for AI-generated content
 * Handles both streaming and final content consistently
 * Based on production-tested parseTranscript logic
 */
export function parseAiResponse(transcriptString, options = {}) {
  const { trackSections = false, currentSection = 'summary' } = options

  if (isProduction) {
    debug('Parsing AI response')
  } else {
    debug('Parsing AI response:', transcriptString?.length ? `${transcriptString.length} characters` : 'empty')
  }

  try {
    if (!transcriptString) {
      return handleEmptyTranscript(trackSections, currentSection)
    }

    const lines = transcriptString.split('\n').map((line) => line.trim())
    const processedResult = processAllLines(lines, currentSection)
    const baseResult = createBaseResult(processedResult)

    if (isProduction) {
      debug('Parsed AI response')
    } else {
      debug('Parsed AI response:', baseResult)
    }

    return finalizeResult(baseResult, processedResult, trackSections, currentSection)
  } catch (error) {
    return handleParsingError(error, transcriptString, trackSections, currentSection)
  }
}

/**
 * Simple hash function to generate compact cache keys
 * Uses djb2 algorithm - fast and good distribution for short strings
 */
// eslint-disable-next-line no-bitwise
function hashString(str) {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    // hash * 33 + char
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) + hash + str.charCodeAt(i)
    // Convert to 32-bit integer
    // eslint-disable-next-line no-bitwise, operator-assignment
    hash = hash & hash
  }
  return hash.toString(36) // Convert to base36 for shorter string
}

/**
 * Creates a memoized parser instance for component-scoped caching
 * This prevents global cache pollution and respects component lifecycle
 */
export function createMemoizedParser() {
  const cache = new Map()
  const MAX_CACHE_SIZE = 50 // More conservative for streaming use case
  const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

  return function parseAiResponseMemoized(transcriptString, options = {}) {
    // Create a compact hash key instead of using the entire transcript
    const cacheKey = hashString(`${transcriptString}_${JSON.stringify(options)}`)
    const now = Date.now()

    // Check cache and TTL
    if (cache.has(cacheKey)) {
      const cached = cache.get(cacheKey)
      if (now - cached.timestamp < CACHE_TTL) {
        debug('Cache hit for AI response parsing')
        // Move to end for LRU behavior (most recently used)
        cache.delete(cacheKey)
        cache.set(cacheKey, cached)
        return cached.result
      }
      // Remove expired entry
      cache.delete(cacheKey)
      debug('Cache entry expired, removing')
    }

    let result
    try {
      result = parseAiResponse(transcriptString, options)
    } catch (error) {
      debug('Parser error in memoized function:', error.message)
      // Don't cache errors, return a descriptive error result object
      return {
        error: true,
        message: `AI response parsing error: ${error.message}`,
        originalInput: transcriptString,
        options,
      }
    }

    // Implement true LRU eviction - remove least recently used (first in Map)
    if (cache.size >= MAX_CACHE_SIZE) {
      const firstKey = cache.keys().next().value
      cache.delete(firstKey)
      debug('Cache size limit reached, evicting least recently used entry')
    }

    cache.set(cacheKey, { result, timestamp: now })
    debug('Cached new AI response parsing result')
    return result
  }
}

/**
 * Default memoized parser for backward compatibility
 * Note: Consider using createMemoizedParser() for new components
 */
export const parseAiResponseMemoized = createMemoizedParser()
